import { cloneDeep, IsEqualCustomizer, isEqualWith, isNull } from "lodash";
import { useState } from "react";

export interface IUnsavedChangesContainer<T> {
  updatedState: T;
  originalState: T;
  changesExist: boolean;
}

export interface IUnsavedChangesDispatchActions<T> {
  set: (state: T) => void;
  update: (state: T | ((prev: T) => T)) => void;
  rollback: () => void;
  save: () => void;
}

export default function useUnsavedChangesContainer<T>(
  isEqualCustomizer?: IsEqualCustomizer,
): [IUnsavedChangesContainer<T>, IUnsavedChangesDispatchActions <T>] {
  const defaultIsEqualCustomizer: IsEqualCustomizer = (value, other) => {
    if ((isNull(value) || value === "") && (isNull(other) || other === "")) {
      return true;
    } else {
      return undefined;
    }
  };

  const [state, setState] = useState<IUnsavedChangesContainer<T>>({
    updatedState: undefined,
    originalState: undefined,
    changesExist: false,
  });

  function set(state: T) {
    setState(prev => ({
      ...prev,
      updatedState: cloneDeep(state),
      originalState: cloneDeep(state),
      changesExist: false,
    }));
  }

  function update(state: T | ((prev: T) => T)) {
    setState(prev => {
      const resultantState: T = typeof state === "function" ? (state as (prev: T) => T)(prev.updatedState) : state;

      return {
        ...prev,
        updatedState: resultantState,
        changesExist: !isEqualWith(
          resultantState,
          prev.originalState,
          isEqualCustomizer ? isEqualCustomizer : defaultIsEqualCustomizer,
        ),
      };
    });
  }

  function rollback() {
    setState(prev => ({
      ...prev,
      updatedState: cloneDeep(state.originalState),
      changesExist: false,
    }));
  }

  function save() {
    setState(prev => ({
      ...prev,
      originalState: cloneDeep(state.updatedState),
      changesExist: false,
    }));
  }

  const actions: IUnsavedChangesDispatchActions<T> = {
    set,
    update,
    rollback,
    save,
  };

  return [state, actions];
}
